import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, AlertTriangle, ShieldCheck } from "lucide-react";
import { getLegalMeta } from "../lib/api";

const OTHER_DOCS = [
  { path: "/legal/terms", label: "Terms of Use" },
  { path: "/legal/privacy", label: "Privacy Notice" },
  { path: "/trust", label: "Trust & Safety" },
  { path: "/legal/data-sources", label: "Data & Third-Party Sources" },
];

// Renders a legal placeholder document with a persistent draft banner.
export const LegalLayout = ({ title, intro, sections, testid, currentPath }) => {
  const [meta, setMeta] = useState(null);
  useEffect(() => { getLegalMeta().then(setMeta).catch(() => {}); }, []);

  return (
    <div data-testid={testid}>
      <section className="bg-atlas-navy text-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14">
          <div className="overline text-atlas-sky mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" /> Legal
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{title}</h1>
          {intro && <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed">{intro}</p>}
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-6 text-xs font-mono text-slate-400">
            <span>Version: {meta?.terms_version || "—"}</span>
            <span>Effective: {meta?.effective_date || "—"}</span>
            <span>Framework: {meta?.jurisdiction || "Netherlands / EU"} · GDPR</span>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-12">
        {/* Draft banner — always visible */}
        <div className="flex items-start gap-3 rounded-md border-2 border-amber-300 bg-amber-50 p-4 mb-10" data-testid="legal-draft-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900 leading-relaxed">
            <span className="font-semibold">Draft — not yet legally reviewed.</span>{" "}
            {meta?.review_notice ||
              "These documents are drafts subject to confirmation by qualified Netherlands/EU counsel before production launch. Atlas is not represented as legally compliant or certified until reviewed."}
          </p>
        </div>

        <div className="space-y-9">
          {sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              <h2 className="font-display text-xl font-bold mb-3">{s.heading}</h2>
              {(Array.isArray(s.body) ? s.body : [s.body]).map((p, j) => (
                <p key={j} className="text-sm text-muted-foreground leading-relaxed mb-3">{p}</p>
              ))}
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-md bg-blue-50/60 border border-atlas-blue/20 p-4 flex items-start gap-3" data-testid="legal-safety-reminder">
          <ShieldCheck className="h-5 w-5 text-atlas-blue shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700 leading-relaxed">
            Atlas will never ask users to send money to secure or purchase a vehicle. Atlas does not
            sell vehicles, process vehicle payments, accept deposits or act as escrow. See{" "}
            <Link to="/trust" className="text-atlas-blue font-medium">Trust &amp; Safety</Link> or{" "}
            <Link to="/report" className="text-atlas-blue font-medium">report an issue</Link>.
          </p>
        </div>

        <div className="mt-10 pt-8 border-t border-border">
          <div className="overline text-muted-foreground mb-3">Related documents</div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {OTHER_DOCS.filter((d) => d.path !== currentPath).map((d) => (
              <Link key={d.path} to={d.path} className="text-atlas-blue hover:underline" data-testid={`legal-link-${d.path.split("/").pop()}`}>
                {d.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
